import React, { type CSSProperties, useCallback } from "react";
import { graphql } from "react-relay";
import type { MultiValueGenericProps, OnChangeValue, OptionProps, Theme } from "react-select";
import AsyncSelect from "react-select/async";

import { faAddressCard, faBuilding, faSuitcase } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { debounce } from "lodash";
import { fetchQuery } from "relay-runtime";

import { zindexModal } from "~/css/zindexes";
import { environment } from "~/models";
import fellowSelectProps from "~/ui/react-select";

import type { HubspotQuery$data } from "~/__generated__/HubspotQuery.graphql";
import type {
	HubspotSelectSearchQuery,
	HubspotSelectSearchQuery$data,
} from "~/__generated__/HubspotSelectSearchQuery.graphql";

import styles from "./css/HubspotSendNotes.module.css";

type HubspotSearchOptionTypeWithOther = HubspotSelectSearchQuery$data["hubspotSearch"][number];
type HubspotSearchOptionType = Exclude<
	HubspotSearchOptionTypeWithOther,
	Extract<HubspotSearchOptionTypeWithOther, { __typename: "%other" }>
>;

type HubspotNoteOptionTypeWithOther = NonNullable<HubspotQuery$data["hubspotContactsForStream"]>[number];
export type HubspotNoteOptionType = Exclude<
	HubspotNoteOptionTypeWithOther,
	Extract<HubspotNoteOptionTypeWithOther, { __typename: "%other" }>
>;

export type HubspotOptionType = HubspotSearchOptionType | HubspotNoteOptionType;

const hubspotSearch = graphql`
	query HubspotSelectSearchQuery($text: String!) {
		hubspotSearch(text: $text) {
			__typename
			... on HubspotContactNode {
				id
				url
				name
				title
				email
				company {
					name
				}
			}
			... on HubspotCompanyNode {
				id
				url
				name
			}
			... on HubspotDealNode {
				id
				url
				name
				amount
			}
		}
	}
`;

function HubspotItem({
	logo,
	title,
	primary,
	secondary,
}: {
	logo: React.ReactNode;
	title: string;
	primary: string;
	secondary?: string | null;
}) {
	return (
		<div className={styles.searchOption}>
			<div className={styles.logoContainer}>
				<div className={styles.logo}>{logo}</div>
			</div>
			<div className={styles.optionText}>
				<div className={styles.optionTitle}>{title}</div>
				<div>
					{primary}
					{secondary ? ` · ${secondary}` : ""}
				</div>
			</div>
		</div>
	);
}

const MultiValueLabel = (props: MultiValueGenericProps<HubspotOptionType, true>) => {
	const data = props.data as HubspotOptionType;
	let content;

	switch (data.__typename) {
		case "HubspotContactPanelNode":
		case "HubspotContactNode":
			content = `Contact · ${data.name}`;
			break;
		case "HubspotCompanyNode":
			content = `Company · ${data.name}`;
			break;
		case "HubspotDealNode":
			content = `Deal · ${data.name}`;
			break;
	}

	return (
		<div {...props.innerProps} className={styles.multiValueLabel} key={`option-${data.id}`}>
			{content}
		</div>
	);
};

const Option = (props: OptionProps<HubspotOptionType, true>) => {
	const data = props.data as HubspotOptionType;
	let content;

	switch (data.__typename) {
		case "HubspotContactPanelNode":
		case "HubspotContactNode":
			content = (
				<HubspotItem
					logo={<FontAwesomeIcon fixedWidth size="lg" icon={faBuilding} />}
					title={data.name}
					primary={"Contact"}
					secondary={data.company?.name && data.email ? `${data.company.name} · ${data.email}` : null}
				/>
			);
			break;
		case "HubspotCompanyNode":
			content = (
				<HubspotItem
					logo={<FontAwesomeIcon fixedWidth size="lg" icon={faSuitcase} />}
					title={data.name}
					primary={"Company"}
					secondary={data.name}
				/>
			);
			break;
		case "HubspotDealNode":
			content = (
				<HubspotItem
					logo={<FontAwesomeIcon fixedWidth size="lg" icon={faAddressCard} />}
					title={data.name}
					primary={"Deal"}
					secondary={data.name}
				/>
			);
			break;
	}

	return (
		<div
			{...props.innerProps}
			key={`option-${data.id}`}
			style={props.getStyles("option", props) as CSSProperties}
			className={props.cx(
				{
					option: true,
					"option--is-disabled": props.isDisabled,
					"option--is-focused": props.isFocused,
					"option--is-selected": props.isSelected,
					"custom--style": true,
				},
				props.className,
			)}
		>
			{content}
		</div>
	);
};

function reactSelectTheme(theme: Theme) {
	return {
		...theme,
		colors: {
			...theme.colors,
			primary25: "var(--veryMutedRoyal)",
			primary: "var(--mutedRoyal)",
		},
	};
}

function getOptionValue(item: HubspotOptionType) {
	return item.id;
}

function handleNoOptionMessage() {
	return "No results found";
}

type HubspotSelectProps = {
	defaultOptions?: HubspotOptionType[];
	setSelectedOptions: (selectedOptions: readonly HubspotOptionType[]) => void;
};

export function HubspotSelect({ defaultOptions = undefined, setSelectedOptions }: HubspotSelectProps) {
	const [inputValue, setInputValue] = React.useState("");

	const loadOptions = React.useCallback((inputValue: string, callback) => {
		fetchQuery<HubspotSelectSearchQuery>(environment, hubspotSearch, { text: inputValue })
			.toPromise()
			.then(data => {
				if (!data) {
					console.error("Unable to fetch options from hubspot");
					return;
				}
				callback(data.hubspotSearch.filter(item => item.__typename !== "%other"));
			});
	}, []);

	const debounceLoadOptions = React.useMemo(() => debounce(loadOptions, 1000), [loadOptions]);

	const handleInputChange = useCallback((val: string) => {
		setInputValue(val);
	}, []);

	const onChangeOption = React.useCallback(
		(options: OnChangeValue<HubspotOptionType, true>) => {
			setSelectedOptions(options ? options : []);
		},
		[setSelectedOptions],
	);

	return (
		<AsyncSelect<HubspotOptionType, true>
			{...fellowSelectProps}
			isMulti
			cacheOptions
			isClearable
			defaultValue={defaultOptions}
			defaultOptions={defaultOptions}
			menuPortalTarget={document.body}
			menuPosition="fixed"
			loadOptions={debounceLoadOptions}
			placeholder={"Select an object…"}
			isSearchable={true}
			components={{ Option, MultiValueLabel }}
			menuPlacement={"auto"}
			onChange={onChangeOption}
			theme={reactSelectTheme}
			getOptionValue={getOptionValue}
			inputValue={inputValue}
			onInputChange={handleInputChange}
			noOptionsMessage={handleNoOptionMessage}
			styles={{
				multiValue: provided => ({
					...provided,
					background: "var(--thirdLayerColor)",
					color: "var(--vividGrey)",
					borderRadius: "14px",
					cursor: "default",
					position: "relative",
					paddingRight: "25px",
				}),
				menuPortal: base => ({ ...base, zIndex: zindexModal + 1 }),
				multiValueRemove: provided => ({
					...provided,
					cursor: "pointer",
					backgroundColor: "transparent",
					position: "absolute",
					right: "4px",
					top: "4px",
					height: "21px",
					borderRadius: "50%",
					paddingLeft: "3.5px",
					paddingRight: "3.5px",

					":hover": {
						background: "var(--mutedGrey)",
						color: "var(--placeholderTextColor)",
					},
				}),
			}}
		/>
	);
}
